<script setup>
import multiselect from "vue-multiselect";
import startupsService from "@/services/startups.service";
import {useStore} from "vue2-helpers/vuex";
import {getNoun} from "@/helpers/string";
import {ref, computed, inject, watch} from "vue";
import trackersCompaniesService from "@/services/trackers-companies.service";

const props = defineProps(['startupId', 'trackersCount']);
const emit = defineEmits(['startupRemovedFromTracker']);

const store = useStore();

const currentTrackerId = inject("currentTrackerId", null);
const setCompaniesListUpdated = inject("setCompaniesListUpdated", null);

const startupTrackers = ref([]);
const trackersPopover = ref(null);
const selectedTracker = ref(null);
const trackersCount = ref(props.trackersCount);
const showPopover = ref(false);
const addedToCurrentTracker = ref(false);
const updating = ref(false);

const actualTrackersList = computed(() => {
    return store.getters.trackersList.filter(tracker =>
        !startupTrackers.value.some(startupTracker => parseInt(startupTracker.id) === parseInt(tracker.id))
        && (currentTrackerId === null || tracker.id !== currentTrackerId)
    )
});

watch(() => props.trackersCount, value => {
    trackersCount.value = value;
})

watch(selectedTracker, (tracker) => {
    if (tracker) {
        addToTracker(tracker.id);
    }
})

async function getCompanyDetails() {
    const {data} = await startupsService.getDetails(props.startupId);
    startupTrackers.value = data.trackers;
    trackersCount.value = data.trackers.length;
}

function handleTrackersClick() {
    if (currentTrackerId) {
        if (!addedToCurrentTracker.value) {
            addToTracker(currentTrackerId.value);
        }
    } else {
        getCompanyDetails();
    }
}

async function addToTracker(trackerId) {
    if (updating.value) {
        return;
    }

    addedToCurrentTracker.value = true;
    updating.value = true;
    await trackersCompaniesService.addCompanies([trackerId], [props.startupId]);

    updating.value = false;
    getCompanyDetails();

    if (currentTrackerId) {
        setCompaniesListUpdated();
    }
}

async function removeFromTracker(trackerId) {
    if (updating.value) {
        return;
    }

    updating.value = true;
    await trackersCompaniesService.removeCompanies(trackerId, [props.startupId]);

    emit('startupRemovedFromTracker');
    updating.value = false;
    getCompanyDetails();
}
</script>

<template>
    <div class="main-table__data" style="display: contents">
        <template>
            <a :id="'trackers-link-' + startupId" class="counter-wrapper">
                <span
                    class="counter"
                    @click="handleTrackersClick"
                >
                    <template v-if="trackersCount">{{trackersCount}}</template>
                    <template v-else>+</template>
                </span>
            </a>
        </template>

        <b-popover
            v-if="!currentTrackerId && store.getters.isEnterprise"
            :show.sync="showPopover"
            ref="trackersPopover"
            :target="'trackers-link-'+startupId"
            placement="bottomright"
        >
            <div
                class="popover__content popover__content--secondary"
                v-click-outside="() => showPopover = false"
            >
                <header class="popover__content__header">
                    <h4 class="heading4">Add to tracker</h4>
                </header>

                <div class="popover__content__body flex-column">
                    <multiselect
                        class="multiselect multiselect-mid"
                        v-model="selectedTracker"
                        :options="actualTrackersList"
                        select-label=""
                        deselect-label=""
                        label="name"
                        track-by="name"
                    >
                    </multiselect>

                    <h4 class="heading4 mt-4" v-if="trackersCount">
                        On {{trackersCount}} {{getNoun('tracker', trackersCount)}}
                    </h4>

                    <ul class="rows-list">
                        <li v-for="tracker in startupTrackers" :key="tracker.id">
                            <h5 class="heading5 heading5--secondary">
                                <a :href="'/trackers/' + tracker.id" target="_blank">{{ tracker.name }}</a>
                            </h5>

                            <a
                                v-if="tracker.type !== 'public'"
                                class="remove-action"
                                @click="removeFromTracker(tracker.id)"
                            >
                                Remove
                            </a>
                        </li>
                    </ul>

                </div>
            </div>
        </b-popover>
    </div>
</template>

<style scoped>

</style>
